(() => {
  const bindOnce = (el, key) => {
    if (!el || el.dataset[key] === 'true') return false;
    el.dataset[key] = 'true';
    return true;
  };

  const youtubeId = (url) => {
    if (!url) return '';
    const match = String(url).match(
      /(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/
    );
    return match ? match[1] : '';
  };

  const vimeoId = (url) => {
    if (!url) return '';
    const match = String(url).match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return match ? match[1] : '';
  };

  const playVideo = (container) => {
    const trigger = container.querySelector('[data-play-inline-trigger]');
    const videoElement = container.querySelector('[data-video-element]');
    if (!trigger || !videoElement) return;

    trigger.style.display = 'none';
    videoElement.style.display = 'block';

    const video = videoElement.querySelector('video');
    if (video) {
      video.play().catch(() => {});
    }

    const template = videoElement.querySelector('[data-video-template]');
    if (template) {
      if (!videoElement.querySelector('iframe')) {
        videoElement.appendChild(template.content.cloneNode(true));
      }
    }
  };

  const pauseVideo = (container) => {
    const trigger = container.querySelector('[data-play-inline-trigger]');
    const videoElement = container.querySelector('[data-video-element]');
    if (!trigger || !videoElement) return;

    const isAutoplay = container.dataset.autoplay === 'true';
    if (isAutoplay) {
      const video = videoElement.querySelector('video');
      if (video) {
        video.pause();
      }
      const iframe = videoElement.querySelector('iframe');
      if (iframe) {
        iframe.remove();
      }
    } else {
      trigger.style.display = '';
      videoElement.style.display = 'none';

      const video = videoElement.querySelector('video');
      if (video) {
        video.pause();
        video.currentTime = 0;
      }

      const iframe = videoElement.querySelector('iframe');
      if (iframe) {
        iframe.remove();
      }
    }
  };

  const initHero = (root) => {
    const hero = root.matches?.('[data-minus-hero]') ? root : root.querySelector('[data-minus-hero]');
    if (!hero || !bindOnce(hero, 'minusBound')) return;

    const slidesContainer = hero.querySelector('.minus-hero__slides');
    const slides = Array.from(hero.querySelectorAll('.minus-hero__slide'));
    const dots = Array.from(hero.querySelectorAll('[data-hero-dot]'));
    const totalSlides = slides.length;
    if (totalSlides === 0) return;

    let currentIndex = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));
    if (currentIndex === -1) currentIndex = 0;

    const isMobile = () => window.innerWidth <= 820;

    const updateSlides = (index) => {
      currentIndex = ((index % totalSlides) + totalSlides) % totalSlides;

      slides.forEach((slide, idx) => {
        slide.classList.toggle('is-active', idx === currentIndex);
      });

      dots.forEach((dot, idx) => {
        dot.classList.toggle('is-active', idx === currentIndex);
      });

      if (slidesContainer) {
        if (isMobile()) {
          slidesContainer.style.transform = `translateX(-${currentIndex * 100}%)`;
        } else {
          slidesContainer.style.transform = '';
        }
      }
    };

    dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.stopPropagation();
        updateSlides(index);
        resetAutoplay();
      });
    });

    // Touch swipe handling for mobile
    let touchStartX = 0;
    let touchStartY = 0;
    let touchEndX = 0;
    let touchEndY = 0;
    let isTouching = false;

    hero.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length !== 1) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchEndX = touchStartX;
        touchEndY = touchStartY;
        isTouching = true;
        stopAutoplay();
      },
      { passive: true }
    );

    hero.addEventListener(
      'touchmove',
      (e) => {
        if (!isTouching || e.touches.length !== 1) return;
        touchEndX = e.touches[0].clientX;
        touchEndY = e.touches[0].clientY;
      },
      { passive: true }
    );

    hero.addEventListener(
      'touchend',
      () => {
        if (!isTouching) return;
        isTouching = false;
        const diffX = touchEndX - touchStartX;
        const diffY = touchEndY - touchStartY;
        if (Math.abs(diffX) > 40 && Math.abs(diffX) > Math.abs(diffY)) {
          if (diffX < 0) {
            updateSlides(currentIndex + 1);
          } else {
            updateSlides(currentIndex - 1);
          }
        }
        startAutoplay();
      },
      { passive: true }
    );

    hero.addEventListener(
      'touchcancel',
      () => {
        isTouching = false;
        startAutoplay();
      },
      { passive: true }
    );

    // Autoplay implementation
    const isAutoplay = hero.dataset.autoplay === 'true';
    const autoplaySpeed = parseInt(hero.dataset.autoplaySpeed, 10) || 5000;
    let autoplayTimer = null;
    let isHovered = false;

    const shouldAutoplay = () => {
      if (!isAutoplay || totalSlides <= 1) return false;
      if (isHovered || isTouching) return false;
      if (document.hidden) return false;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
      return true;
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (!shouldAutoplay()) return;
      autoplayTimer = setInterval(() => {
        if (!shouldAutoplay()) return;
        updateSlides(currentIndex + 1);
      }, autoplaySpeed);
    };

    const stopAutoplay = () => {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    };

    const resetAutoplay = () => {
      stopAutoplay();
      startAutoplay();
    };

    if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
      hero.addEventListener('mouseenter', () => {
        isHovered = true;
        stopAutoplay();
      });

      hero.addEventListener('mouseleave', () => {
        isHovered = false;
        startAutoplay();
      });
    }

    const onVisibilityChange = () => {
      if (document.hidden) {
        stopAutoplay();
      } else {
        startAutoplay();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    const onResize = () => {
      updateSlides(currentIndex);
    };
    window.addEventListener('resize', onResize);

    hero._cleanupHero = () => {
      stopAutoplay();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('resize', onResize);
    };

    updateSlides(currentIndex);
    startAutoplay();
  };

  const initSkin = (root) => {
    const skin = root.matches?.('[data-minus-skin]') ? root : root.querySelector('[data-minus-skin]');
    if (!skin || !bindOnce(skin, 'minusBound')) return;

    const track = skin.querySelector('[data-skin-track]');
    const slides = Array.from(skin.querySelectorAll('[data-skin-slide]'));
    const prevBtn = skin.querySelector('[data-skin-prev]');
    const nextBtn = skin.querySelector('[data-skin-next]');
    const mobileLabel = skin.querySelector('[data-skin-mobile-label]');
    if (!track || !slides.length) return;

    const container = track.parentElement;
    if (!container) return;

    let activeIndex = Math.max(
      0,
      slides.findIndex((slide) => slide.classList.contains('is-active'))
    );

    const updateCarousel = (isImmediate = false) => {
      slides.forEach((slide, idx) => {
        slide.classList.toggle('is-active', idx === activeIndex);
      });

      const panels = Array.from(skin.querySelectorAll('[data-review-panel]'));
      if (panels.length) {
        panels.forEach((panel, idx) => {
          const isActive = idx === activeIndex;
          panel.classList.toggle('is-active', isActive);

          const videoContainer = panel.querySelector('[data-video-container]');
          if (videoContainer) {
            if (isActive) {
              const isAutoplay = videoContainer.dataset.autoplay === 'true';
              if (isAutoplay) {
                playVideo(videoContainer);
              }
            } else {
              pauseVideo(videoContainer);
            }
          }
        });
      }

      if (mobileLabel && slides[activeIndex]) {
        mobileLabel.textContent = slides[activeIndex].dataset.label || '';
      }

      const activeSlide = slides[activeIndex];
      const containerWidth = container.clientWidth;
      const slideWidth = activeSlide.clientWidth;
      const slideLeft = activeSlide.offsetLeft;
      let scrollOffset = slideLeft - containerWidth / 2 + slideWidth / 2;
      const maxScroll = container.scrollWidth - containerWidth;

      if (maxScroll <= 0) {
        scrollOffset = 0;
      } else if (scrollOffset < 0) {
        scrollOffset = 0;
      } else if (scrollOffset > maxScroll) {
        scrollOffset = maxScroll;
      }

      container.scrollTo({
        left: scrollOffset,
        behavior: isImmediate ? 'auto' : 'smooth'
      });
    };

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        activeIndex = (activeIndex + 1) % slides.length;
        updateCarousel();
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        activeIndex = (activeIndex - 1 + slides.length) % slides.length;
        updateCarousel();
      });
    }

    let isDown = false;
    let startX;
    let scrollLeftVal;
    let hasMoved = false;

    container.addEventListener('mousedown', (e) => {
      isDown = true;
      startX = e.pageX - container.offsetLeft;
      scrollLeftVal = container.scrollLeft;
      hasMoved = false;
    });

    container.addEventListener('mouseleave', () => {
      isDown = false;
    });

    container.addEventListener('mouseup', () => {
      isDown = false;
    });

    container.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 1.5;
      if (Math.abs(walk) > 5) {
        hasMoved = true;
      }
      container.scrollLeft = scrollLeftVal - walk;
    });

    slides.forEach((slide, idx) => {
      slide.addEventListener('click', (e) => {
        if (hasMoved) {
          e.preventDefault();
          return;
        }
        activeIndex = idx;
        updateCarousel();
      });
    });

    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => updateCarousel(true), 100);
    });

    updateCarousel(true);
  };

  const embedHtml = (url) => {
    const yt = youtubeId(url);
    if (yt) {
      return `<iframe src="https://www.youtube.com/embed/${yt}?autoplay=1" allow="autoplay; encrypted-media" allowfullscreen title="Review video"></iframe>`;
    }
    const vimeo = vimeoId(url);
    if (vimeo) {
      return `<iframe src="https://player.vimeo.com/video/${vimeo}?autoplay=1" allow="autoplay; fullscreen" allowfullscreen title="Review video"></iframe>`;
    }
    return '';
  };

  const initReviews = (root) => {
    const reviews = root.matches?.('[data-minus-reviews]') ? root : root.querySelector('[data-minus-reviews]');
    if (!reviews || !bindOnce(reviews, 'minusBound')) return;

    const tabs = Array.from(reviews.querySelectorAll('[data-review-tab]'));
    const panels = Array.from(reviews.querySelectorAll('[data-review-panel]'));
    const modal = reviews.querySelector('[data-review-modal]');
    const modalFrame = reviews.querySelector('[data-review-modal-frame]');
    const modalClose = reviews.querySelector('[data-review-modal-close]');

    const setActive = (index) => {
      tabs.forEach((tab, idx) => {
        tab.classList.toggle('is-active', idx === index);
        tab.setAttribute('aria-selected', idx === index ? 'true' : 'false');
      });
      panels.forEach((panel, idx) => {
        panel.classList.toggle('is-active', idx === index);
      });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => setActive(index));
    });

    const closeModal = () => {
      if (!modal) return;
      modal.classList.remove('is-open');
      modal.hidden = true;
      if (modalFrame) modalFrame.innerHTML = '';
      document.body.style.overflow = '';
    };

    const openModal = (panel) => {
      if (!modal || !modalFrame || !panel) return;
      const url = panel.dataset.reviewVideo || '';
      const name = panel.dataset.reviewName || 'Review video';
      const html = embedHtml(url);
      modalFrame.innerHTML =
        html ||
        `<p class="minus-reviews__quote">${name}</p>`;
      modal.hidden = false;
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    };

    reviews.querySelectorAll('[data-review-play]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = button.closest('[data-review-panel]');
        openModal(panel);
      });
    });

    if (modalClose) modalClose.addEventListener('click', closeModal);
    if (modal) {
      modal.addEventListener('click', (event) => {
        if (event.target === modal) closeModal();
      });
    }
  };

  const initInlineVideos = (root) => {
    const containers = Array.from(root.querySelectorAll('[data-video-container]'));
    containers.forEach((container) => {
      if (!bindOnce(container, 'inlineVideoBound')) return;

      const trigger = container.querySelector('[data-play-inline-trigger]');
      if (!trigger) return;

      trigger.addEventListener('click', () => {
        playVideo(container);
      });

      const isAutoplay = container.dataset.autoplay === 'true';
      if (isAutoplay) {
        const videoElement = container.querySelector('[data-video-element]');
        if (videoElement) {
          trigger.style.display = 'none';
          videoElement.style.display = 'block';
        }

        // If the parent panel is already active, play it now
        const panel = container.closest('[data-review-panel]');
        if (panel && panel.classList.contains('is-active')) {
          playVideo(container);
        }
      }
    });
  };

  const initAll = (root = document) => {
    initHero(root);
    initSkin(root);
    initReviews(root);
    initInlineVideos(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const hero = event.target?.matches?.('[data-minus-hero]')
      ? event.target
      : event.target?.querySelector?.('[data-minus-hero]');
    if (hero && typeof hero._cleanupHero === 'function') {
      hero._cleanupHero();
    }
  });
})();
